import { useQuery, UseQueryResult } from "@tanstack/react-query";
import api from "@/config/axiosConfig";
import { WeatherResponse } from "@/types/weather";
import { Location } from "@/types/trip";

/**
 * Hook to fetch weather data for a location
 *
 * @param selectedLocation The location object to fetch weather for
 * @param startDate Optional start date for weather data
 * @param endDate Optional end date for weather data
 * @returns Query result containing weather data
 */
export const useWeather = (
  selectedLocation: Location | null,
  startDate?: string,
  endDate?: string
): UseQueryResult<WeatherResponse | null, Error> => {
  return useQuery({
    queryKey: ["weather", selectedLocation?.id, startDate, endDate],
    queryFn: async () => {
      if (!selectedLocation) {
        return null;
      }

      // Use trip dates or fallback to current date range
      const start =
        startDate ||
        selectedLocation?.date?.start_date ||
        new Date().toISOString().split("T")[0]; // Already in YYYY-MM-DD format
      const end =
        endDate ||
        selectedLocation?.date?.end_date ||
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0]; // Already in YYYY-MM-DD format

      const response = await api.content.get<WeatherResponse>("/weather", {
        params: {
          lat: selectedLocation.coordinates?.latitude,
          lon: selectedLocation.coordinates?.longitude,
          startDate: start,
          endDate: end,
        },
      });

      return response.data;
    },
    enabled: !!selectedLocation && !!selectedLocation.coordinates,
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes
  });
};
